import { useMemo } from 'react';
import type { MultiselectCustomerPartialEntity } from '../../../components/global/ConfigurationMultiselect/configurationMultiselectTypes';
import { ConfigurationRuleScopeEntityType } from '@ydistri/api-sdk';
import { useScope } from './useScope';
import { useMainScope } from './useMainScope';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../store';
import {
  filterStoresByRegions,
  filterStoreTypesWithStores,
  filterTypedEntitiesByScope,
  isEntityInScope,
  isMainScope,
  isModalViewMode,
  ViewMode,
} from './scopeUtilsLib';
import {
  useGetDepartmentsQuery,
  useGetRegionsQuery,
  useGetStoresQuery,
  useGetStoreTypesQuery,
} from '../../../apis/apiLists';
import { customerIDThenTitleSorter } from './storesLib';
import { useDisplayStoreLabelFormatter } from '../../../hooks/useDisplayStoreLabel';
import { TemplateOrCalculationWithValue } from '../../../apis/api';

interface UserScopeOptionsDataResponse {
  regionOptions: MultiselectCustomerPartialEntity[];
  storeOptions: MultiselectCustomerPartialEntity[];
  allStoreOptions: MultiselectCustomerPartialEntity[];
  storeTypeOptions: MultiselectCustomerPartialEntity[];
  departmentOptions: MultiselectCustomerPartialEntity[];
  loading: boolean;
}

export function useScopeOptionsData(
  scopeId: number,
  viewMode: ViewMode,
  templateOrCalculation: TemplateOrCalculationWithValue,
): UserScopeOptionsDataResponse {
  const mainScope = useMainScope(templateOrCalculation);
  const scopeById = useScope(scopeId, templateOrCalculation);
  const modalScope = useSelector((state: ReduxState) => state.scope.modalScope);
  const storeLabelFormatter = useDisplayStoreLabelFormatter();

  const { data: availableRegions, isFetching: isFetchingRegions } = useGetRegionsQuery(
    templateOrCalculation.value,
  );
  const { data: availableStores, isFetching: isFetchingStores } = useGetStoresQuery(
    templateOrCalculation.value,
  );
  const { data: availableStoreTypes, isFetching: isFetchingStoreTypes } = useGetStoreTypesQuery(
    templateOrCalculation.value,
  );
  const { data: availableDepartments, isFetching: isFetchingDepartments } = useGetDepartmentsQuery(
    templateOrCalculation.value,
  );

  const scope = useMemo(
    () => (isModalViewMode(viewMode) ? modalScope : scopeById),
    [modalScope, scopeById, viewMode],
  );

  const pgRegionSelectionOption =
    mainScope?.entities[ConfigurationRuleScopeEntityType.Regions].selectionOption;
  const pgRegionSelectedIds =
    mainScope?.entities[ConfigurationRuleScopeEntityType.Regions].selectedIds;

  const pgStoreTypeSelectionOption =
    mainScope?.entities[ConfigurationRuleScopeEntityType.StoreTypes].selectionOption;
  const pgStoreTypeSelectedIds =
    mainScope?.entities[ConfigurationRuleScopeEntityType.StoreTypes].selectedIds;

  const pgStoreSelectionOption =
    mainScope?.entities[ConfigurationRuleScopeEntityType.Stores].selectionOption;
  const pgStoreSelectedIds =
    mainScope?.entities[ConfigurationRuleScopeEntityType.Stores].selectedIds;

  const pgDepartmentSelectionOption =
    mainScope?.entities[ConfigurationRuleScopeEntityType.Departments].selectionOption;
  const pgDepartmentSelectedIds =
    mainScope?.entities[ConfigurationRuleScopeEntityType.Departments].selectedIds;

  const curRegionSelectionOption =
    scope?.entities[ConfigurationRuleScopeEntityType.Regions].selectionOption;
  const curRegionSelectedIds =
    scope?.entities[ConfigurationRuleScopeEntityType.Regions].selectedIds;

  const curStoreTypeSelectionOption =
    scope?.entities[ConfigurationRuleScopeEntityType.StoreTypes].selectionOption;
  const curStoreTypeSelectedIds =
    scope?.entities[ConfigurationRuleScopeEntityType.StoreTypes].selectedIds;

  const isFetching =
    isFetchingRegions || isFetchingStores || isFetchingStoreTypes || isFetchingDepartments;
  const isPlaygroundModal = isMainScope(scope) && viewMode === ViewMode.ADJUST_SCOPE_MODAL;

  //if not in playground, apply playground regions filters (All / specified / excluding filter)
  const finalRegions = useMemo(() => {
    const regions = availableRegions?.array ?? [];

    const selectionOption = isPlaygroundModal ? curRegionSelectionOption : pgRegionSelectionOption;
    const selectedIds = (isPlaygroundModal ? curRegionSelectedIds : pgRegionSelectedIds) ?? [];

    if (!selectionOption) return regions;

    return filterTypedEntitiesByScope(regions, selectionOption, selectedIds);
  }, [
    availableRegions?.array,
    isPlaygroundModal,
    curRegionSelectionOption,
    pgRegionSelectionOption,
    curRegionSelectedIds,
    pgRegionSelectedIds,
  ]);

  const finalRegionsForStores = useMemo(() => {
    if (!curRegionSelectionOption) return finalRegions;

    return filterTypedEntitiesByScope(
      finalRegions,
      curRegionSelectionOption,
      curRegionSelectedIds ?? [],
    );
  }, [finalRegions, curRegionSelectionOption, curRegionSelectedIds]);

  const regionEnabledStores = useMemo(() => {
    return filterStoresByRegions(availableStores?.array ?? [], finalRegionsForStores);
  }, [availableStores?.array, finalRegionsForStores]);

  //if not in playground, apply playground store type filters (All / specified / excluding filter)
  const finalStoreTypes = useMemo(() => {
    let storeTypes = filterStoreTypesWithStores(
      availableStoreTypes?.array ?? [],
      regionEnabledStores,
    );

    if (!isMainScope(scope) && pgStoreTypeSelectionOption) {
      storeTypes = filterTypedEntitiesByScope(
        storeTypes,
        pgStoreTypeSelectionOption,
        pgStoreTypeSelectedIds ?? [],
      );
    }

    return storeTypes;
  }, [
    availableStoreTypes?.array,
    scope,
    pgStoreTypeSelectionOption,
    regionEnabledStores,
    pgStoreTypeSelectedIds,
  ]);

  const finalDepartments = useMemo(() => {
    let departments = availableDepartments?.array ?? [];

    if (!isMainScope(scope) && pgDepartmentSelectionOption) {
      departments = filterTypedEntitiesByScope(
        departments,
        pgDepartmentSelectionOption,
        pgDepartmentSelectedIds ?? [],
      );
    }

    return departments;
  }, [availableDepartments?.array, scope, pgDepartmentSelectionOption, pgDepartmentSelectedIds]);

  const finalStores = useMemo(() => {
    let stores = regionEnabledStores;

    if (
      !isMainScope(scope) &&
      pgStoreSelectionOption &&
      pgStoreTypeSelectionOption &&
      pgDepartmentSelectionOption &&
      pgRegionSelectionOption
    ) {
      stores = stores.filter(s =>
        isEntityInScope(s.region?.id ?? 0, pgRegionSelectionOption, pgRegionSelectedIds ?? []),
      );
      stores = stores.filter(s =>
        isEntityInScope(
          s.storeType?.id ?? 0,
          pgStoreTypeSelectionOption,
          pgStoreTypeSelectedIds ?? [],
        ),
      );
      stores = filterTypedEntitiesByScope(stores, pgStoreSelectionOption, pgStoreSelectedIds ?? []);
    }

    if (curStoreTypeSelectionOption) {
      stores = stores.filter(s =>
        isEntityInScope(
          s.storeType?.id ?? 0,
          curStoreTypeSelectionOption,
          curStoreTypeSelectedIds ?? [],
        ),
      );
    }

    if (curRegionSelectionOption) {
      stores = stores.filter(s =>
        isEntityInScope(s.region?.id ?? 0, curRegionSelectionOption, curRegionSelectedIds ?? []),
      );
    }

    return stores;
  }, [
    regionEnabledStores,
    scope,
    pgStoreSelectionOption,
    pgStoreTypeSelectionOption,
    pgDepartmentSelectionOption,
    pgRegionSelectionOption,
    curStoreTypeSelectionOption,
    curRegionSelectionOption,
    pgRegionSelectedIds,
    pgStoreTypeSelectedIds,
    pgStoreSelectedIds,
    curStoreTypeSelectedIds,
    curRegionSelectedIds,
  ]);

  const storeOptions = useMemo(
    (): MultiselectCustomerPartialEntity[] =>
      finalStores
        .map(s => ({
          id: s.id,
          title: s.name,
          code: s.code,
          customerId: s.customerStoreId ?? '',
          label: storeLabelFormatter(s.name, s.customerStoreId, s.code),
        }))
        .sort(customerIDThenTitleSorter),
    [finalStores, storeLabelFormatter],
  );

  const allStoreOptions = useMemo(
    (): MultiselectCustomerPartialEntity[] =>
      (availableStores?.array ?? [])
        .map(s => ({
          id: s.id,
          title: s.name,
          code: s.code,
          customerId: s.customerStoreId ?? '',
          label: storeLabelFormatter(s.name, s.customerStoreId, s.code),
        }))
        .sort(customerIDThenTitleSorter),
    [availableStores?.array, storeLabelFormatter],
  );

  const regionOptions = useMemo(
    (): MultiselectCustomerPartialEntity[] =>
      ((isMainScope(scope) ? availableRegions?.array : finalRegions) ?? []).map(r => ({
        id: r.id,
        title: r.name,
        customerId: r.customerRegionId ?? '',
      })),
    [availableRegions?.array, finalRegions, scope],
  );

  const storeTypeOptions = useMemo(
    (): MultiselectCustomerPartialEntity[] =>
      finalStoreTypes.map(s => ({
        id: s.id,
        title: s.name,
        customerId: s.customerWarehouseTypeId ?? '',
      })),
    [finalStoreTypes],
  );

  const departmentOptions = useMemo(
    (): MultiselectCustomerPartialEntity[] =>
      finalDepartments
        .map(d => ({
          id: d.id,
          title: d.name,
          customerId: d.customerDepartmentId ?? '',
        }))
        .sort(customerIDThenTitleSorter),
    [finalDepartments],
  );

  return useMemo(
    () => ({
      storeOptions,
      allStoreOptions,
      regionOptions,
      storeTypeOptions,
      departmentOptions,
      loading: isFetching,
    }),
    [allStoreOptions, departmentOptions, isFetching, regionOptions, storeOptions, storeTypeOptions],
  );
}
